import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, UserCog, Search } from "lucide-react";

export default function Users() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "operations", client_ids: [] });

  const load = async () => {
    const [u, c] = await Promise.all([api.get("/users"), api.get("/clients")]);
    setItems(u.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ email: "", name: "", password: "", role: "operations", client_ids: [] });
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email, name: u.name, password: "",
      role: u.role,
      client_ids: u.client_ids || (u.client_id ? [u.client_id] : []),
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.role !== "client") payload.client_ids = [];
      if (editing) {
        const { password, email, ...rest } = payload;
        const patch = password ? { ...rest, password } : rest;
        await api.patch(`/users/${editing.id}`, patch);
        toast.success("User updated");
      } else {
        await api.post("/users", payload);
        toast.success("User created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleClient = (id) => {
    setForm((f) => ({
      ...f,
      client_ids: f.client_ids.includes(id) ? f.client_ids.filter((x) => x !== id) : [...f.client_ids, id],
    }));
  };

  return (
    <div data-testid="page-users" className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Admin</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">Users</h1>
          <p className="text-sm text-slate-500 mt-1">SRAD roles can create, edit, delete. Client users get view-only access to their linked clients.</p>
        </div>
        <button data-testid="btn-new-user" onClick={openNew} className="bg-[var(--navy)] hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2.5 rounded-md flex items-center gap-2">
          <Plus size={16} /> New User
        </button>
      </div>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2 max-w-xl">
        <Search size={14} className="text-slate-400" />
        <input
          data-testid="input-user-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, role, linked client…"
          className="flex-1 outline-none text-sm bg-transparent"
        />
        {q && <button onClick={() => setQ("")} className="text-xs text-slate-500 hover:text-slate-700">Clear</button>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 text-left">
              <th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Role</th><th className="px-5 py-3">Linked Clients</th>
              <th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const qq = q.trim().toLowerCase();
              const visible = qq
                ? items.filter((u) => {
                    const linkedIds = (u.client_ids && u.client_ids.length ? u.client_ids : (u.client_id ? [u.client_id] : []));
                    const linkedNames = linkedIds.map((id) => clients.find((c) => c.id === id)?.name).filter(Boolean).join(" ");
                    return [u.name, u.email, u.role, u.is_active ? "active" : "inactive", linkedNames]
                      .filter(Boolean).some((v) => String(v).toLowerCase().includes(qq));
                  })
                : items;
              return (<>
              {visible.map((u) => {
              const linked = (u.client_ids && u.client_ids.length ? u.client_ids : (u.client_id ? [u.client_id] : []));
              const linkedNames = linked.map((id) => clients.find((c) => c.id === id)?.name).filter(Boolean);
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="table-row border-t border-slate-100">
                  <td className="px-5 py-3 font-medium">{u.name}</td>
                  <td className="px-5 py-3 text-slate-600">{u.email}</td>
                  <td className="px-5 py-3"><span className="text-xs px-2 py-1 rounded-full bg-slate-100 capitalize">{(u.role || "").replace("_", " ")}</span></td>
                  <td className="px-5 py-3 text-slate-600 text-xs">
                    {u.role === "client"
                      ? (linkedNames.length ? linkedNames.join(" · ") : <span className="text-rose-600">not linked</span>)
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-5 py-3"><span className={`text-xs font-semibold ${u.is_active ? "text-emerald-700" : "text-slate-500"}`}>{u.is_active ? "active" : "inactive"}</span></td>
                  <td className="px-5 py-3 text-right">
                    <button data-testid={`btn-edit-user-${u.id}`} onClick={() => openEdit(u)} className="text-xs text-amber-700 hover:text-amber-900 font-semibold inline-flex items-center gap-1">
                      <UserCog size={12} /> Edit
                    </button>
                  </td>
                </tr>
              );
            })}
              {visible.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-slate-500">{q ? `No matches for "${q}"` : "No users yet"}</td></tr>}
              </>);
            })()}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-40 p-6" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[85vh] overflow-y-auto" data-testid="dialog-user">
            <h2 className="font-heading text-xl font-bold mb-4">{editing ? "Edit User" : "New User"}</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Email</label>
                <input data-testid="user-email" required type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm disabled:bg-slate-100" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Full Name</label>
                <input data-testid="user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">{editing ? "New Password (leave blank to keep)" : "Password"}</label>
                <input data-testid="user-password" type="password" required={!editing} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm">
                  <option value="super_admin">Super Admin</option>
                  <option value="operations">Operations (SRAD)</option>
                  <option value="sales">Sales (SRAD)</option>
                  <option value="accounts">Accounts (SRAD)</option>
                  <option value="client">Client User (view-only)</option>
                </select>
                <div className="text-xs text-slate-500 mt-1">SRAD roles have create/edit/delete. Client users have view-only access to linked clients and can raise queries.</div>
              </div>

              {form.role === "client" && (
                <div className="col-span-2">
                  <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Linked Clients (select one or more)</label>
                  <div className="mt-2 border border-slate-200 rounded-md p-3 max-h-56 overflow-y-auto space-y-1">
                    {clients.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:bg-slate-50 px-2 rounded">
                        <input type="checkbox" checked={form.client_ids.includes(c.id)} onChange={() => toggleClient(c.id)} data-testid={`user-client-${c.id}`} />
                        <span className="font-mono text-xs text-slate-500">{c.code}</span>
                        <span>{c.name}</span>
                      </label>
                    ))}
                    {clients.length === 0 && <div className="text-sm text-slate-500">No clients yet. Create clients first.</div>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{form.client_ids.length} client{form.client_ids.length === 1 ? "" : "s"} selected</div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button data-testid="btn-save-user" className="px-4 py-2 text-sm bg-[var(--navy)] text-white rounded-md">{editing ? "Save" : "Create"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
